// The one door: @aiw/api-client over services/api. In mock mode the same client
// talks to src/mock/server.ts through the fetch seam, so every page is built
// against the real contract from day 1.
import { createApiClient, api as calls, type ApiClient } from "@aiw/api-client";
import { env } from "./env";
import { getToken } from "./auth";
import { getTestSet } from "./testset";
import { mockFetch } from "../mock/server";

export const fetchImpl: typeof fetch = env.mock ? mockFetch : (input, init) => fetch(input, init);

export const client: ApiClient = createApiClient({
  baseUrl: env.apiBase,
  surface: "crm",
  getToken,
  testSetId: getTestSet,
  fetch: fetchImpl,
});

export const api = calls;

/** Headers for hand-rolled requests (the SSE reader). */
export async function authHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = { "x-aiw-surface": "crm" };
  const t = await getToken();
  if (t) h.authorization = `Bearer ${t}`;
  const ts = getTestSet();
  if (ts) h["x-aiw-test-set"] = ts;
  return h;
}

/** Open an address on a map. The old platform built exactly this link inside its work-order HTML
 *  (`google.com/maps/search/?api=1&query=…`), which is why nobody had to type an address twice — and
 *  it is the one platform behaviour the CRM never carried over. Same helper the installer portal has. */
export const mapsLink = (address: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

/**
 * THERE IS NO OLD-PLATFORM LINK HELPER ANY MORE, AND THERE MUST NOT BE ONE AGAIN.
 *
 * Jacky, 17 September: *"I dont want to see a link to old platform (any historical data, if can
 * transform do it, or make it read only view, if quote PDF generated, the link to blob can be added
 * direct)"* — and in about thirty days the platform's containers are decommissioned, so a link to it
 * is a link to a 404.
 *
 * Every former call site now goes somewhere real:
 *   · a job            → `/jobs/:ref`, which this CRM owns
 *   · a quote          → `/quotes/:id`, a read-only view of the historical quote, with the generated
 *                        PDF linked DIRECTLY (`pdf_share_url`), never through the platform's page
 *   · purchase orders  → `/purchase-orders`, built from `public.pos`
 *   · a work order     → the job's own install days, in this CRM
 *   · the customer portal — a customer surface, not a staff one — removed from the staff UI
 * `apps/crm/src/lib/platform-links.test.ts` now FAILS if any of this comes back — including a helper, a hard-coded host, or a leftover env base.
 */
